import React, { useEffect, useState } from 'react';
import { useParams, useOutletContext } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Progress } from '../components/ui/progress';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  Plus, ShoppingCart, Check, Loader2, DollarSign, 
  Package, Receipt, Trash2, ShoppingBag
} from 'lucide-react';
import { toast } from 'sonner';
import { ScreenHelp } from '../components/common/ScreenHelp';
import { MobileFormModal, MobileFormFooter } from '../components/common/MobileFormModal';
import { LabelWithTooltip, FIELD_TIPS } from '../components/common/FieldTooltip';

export default function TripPurchases() {
  const { tripId } = useParams();
  const context = useOutletContext();
  const { isOrganizer, members } = context || {};
  const { api, user } = useAuth();
  
  const [purchases, setPurchases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [markDialogOpen, setMarkDialogOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  
  const [formData, setFormData] = useState({
    name: '',
    quantity: 1,
    unit: 'un',
    estimated_price: '',
    purchase_type: 'common',
    notes: '',
  });
  const [markPrice, setMarkPrice] = useState('');
  
  // Detect mobile
  const [isMobile, setIsMobile] = useState(false);
  
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 640);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    fetchPurchases();
  }, [tripId]);

  const fetchPurchases = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/trips/${tripId}/purchases`);
      setPurchases(response.data);
    } catch (error) {
      console.error('Error fetching purchases:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e?.preventDefault();
    if (!formData.name) {
      toast.error('Digite o nome do item');
      return;
    }
    try {
      await api.post(`/trips/${tripId}/purchases`, {
        ...formData,
        trip_id: tripId,
        estimated_price: formData.estimated_price ? parseFloat(formData.estimated_price) : null,
      });
      toast.success('Item adicionado à lista!');
      setDialogOpen(false);
      setFormData({ name: '', quantity: 1, unit: 'un', estimated_price: '', purchase_type: 'common', notes: '' });
      fetchPurchases();
    } catch (error) {
      toast.error('Erro ao adicionar item');
    }
  };

  const handleMarkPurchased = async (e) => {
    e.preventDefault();
    if (!selectedItem || !markPrice) return;
    try {
      await api.put(`/trips/${tripId}/purchases/${selectedItem.id}`, {
        status: 'purchased',
        actual_price: parseFloat(markPrice),
      });
      toast.success('Item marcado como comprado!');
      setMarkDialogOpen(false);
      setSelectedItem(null);
      setMarkPrice('');
      fetchPurchases();
    } catch (error) {
      toast.error('Erro ao marcar como comprado');
    }
  };

  const handleDelete = async (itemId) => {
    if (!window.confirm('Excluir este item?')) return;
    try {
      await api.delete(`/trips/${tripId}/purchases/${itemId}`);
      toast.success('Item excluído');
      fetchPurchases();
    } catch (error) {
      toast.error('Erro ao excluir');
    }
  };

  const openMarkDialog = (item) => {
    setSelectedItem(item);
    setMarkPrice(item.estimated_price?.toString() || '');
    setMarkDialogOpen(true);
  };

  // Calculate summary
  const totalItems = purchases.length;
  const purchasedItems = purchases.filter(p => p.status === 'purchased').length;
  const pendingItems = totalItems - purchasedItems;
  const estimatedTotal = purchases.reduce((acc, p) => acc + (p.estimated_price || 0), 0);
  const actualTotal = purchases.reduce((acc, p) => acc + (p.actual_price || 0), 0);
  const progressPercent = totalItems > 0 ? (purchasedItems / totalItems) * 100 : 0;

  const pendingPurchases = purchases.filter(p => p.status === 'pending');
  const completedPurchases = purchases.filter(p => p.status === 'purchased');

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-heading font-bold text-xl text-slate-900 dark:text-white">Lista de Compras</h2>
          <p className="text-slate-500 dark:text-slate-400">Organize o que precisa ser comprado para a viagem</p>
        </div>
        <div className="flex gap-2">
          <ScreenHelp screen="purchases" />
          {/* Mobile Modal */}
          {isMobile ? (
            <>
              <Button 
                className="rounded-full bg-orange-500 hover:bg-orange-600" 
                onClick={() => setDialogOpen(true)}
                data-testid="add-purchase-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Adicionar
              </Button>
              
              <MobileFormModal
                isOpen={dialogOpen}
                onClose={() => setDialogOpen(false)}
                title="Adicionar item"
                footer={
                  <MobileFormFooter
                    onCancel={() => setDialogOpen(false)}
                    onSubmit={handleCreate}
                    cancelText="Cancelar"
                    submitText="Adicionar"
                  />
                }
              >
                <form onSubmit={handleCreate} className="px-4 py-4 space-y-4">
                  <div className="py-2">
                    <LabelWithTooltip tip={FIELD_TIPS.purchases.name} required>
                      Nome do item
                    </LabelWithTooltip>
                    <input
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="Ex: Cerveja, Carvão, Gelo..."
                      className="w-full bg-transparent border-0 border-b border-slate-200 dark:border-slate-700 focus:border-orange-500 focus:ring-0 px-0 py-3 text-base placeholder:text-slate-400 text-slate-900 dark:text-white outline-none"
                      data-testid="purchase-name-mobile"
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="py-2">
                      <LabelWithTooltip tip={FIELD_TIPS.purchases.quantity}>
                        Quantidade
                      </LabelWithTooltip>
                      <input
                        type="number"
                        value={formData.quantity}
                        onChange={(e) => setFormData({ ...formData, quantity: parseInt(e.target.value) || 1 })}
                        min={1}
                        className="w-full bg-transparent border-0 border-b border-slate-200 dark:border-slate-700 focus:border-orange-500 focus:ring-0 px-0 py-2 text-base text-slate-900 dark:text-white outline-none"
                      />
                    </div>
                    <div className="py-2">
                      <label className="text-sm font-medium text-slate-700 dark:text-slate-300">Unidade</label>
                      <Select
                        value={formData.unit}
                        onValueChange={(v) => setFormData({ ...formData, unit: v })}
                      >
                        <SelectTrigger className="w-full bg-transparent border-0 border-b border-slate-200 dark:border-slate-700 focus:border-orange-500 focus:ring-0 px-0 py-2 text-base h-auto rounded-none shadow-none">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="un">Unidade</SelectItem>
                          <SelectItem value="kg">Kg</SelectItem>
                          <SelectItem value="L">Litro</SelectItem>
                          <SelectItem value="cx">Caixa</SelectItem>
                          <SelectItem value="pct">Pacote</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  
                  <div className="py-2">
                    <LabelWithTooltip tip={FIELD_TIPS.purchases.estimated}>
                      Preço estimado (R$)
                    </LabelWithTooltip>
                    <div className="relative">
                      <span className="absolute left-0 top-1/2 -translate-y-1/2 text-slate-400">R$</span>
                      <input
                        type="number"
                        value={formData.estimated_price}
                        onChange={(e) => setFormData({ ...formData, estimated_price: e.target.value })}
                        placeholder="0,00"
                        className="w-full bg-transparent border-0 border-b border-slate-200 dark:border-slate-700 focus:border-orange-500 focus:ring-0 pl-8 py-3 text-base placeholder:text-slate-400 text-slate-900 dark:text-white outline-none"
                      />
                    </div>
                  </div>
                  
                  <div className="py-2">
                    <LabelWithTooltip tip={FIELD_TIPS.purchases.notes}>
                      Observações
                    </LabelWithTooltip>
                    <input
                      value={formData.notes}
                      onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                      placeholder="Marca preferida, onde comprar..."
                      className="w-full bg-transparent border-0 border-b border-slate-200 dark:border-slate-700 focus:border-orange-500 focus:ring-0 px-0 py-3 text-base placeholder:text-slate-400 text-slate-900 dark:text-white outline-none"
                    />
                  </div>
                </form>
              </MobileFormModal>
            </>
          ) : (
            /* Desktop Modal */
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button className="rounded-full bg-orange-500 hover:bg-orange-600" data-testid="add-purchase-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Adicionar item
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Adicionar item à lista</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleCreate} className="space-y-4 mt-4">
                  <div>
                    <LabelWithTooltip tip={FIELD_TIPS.purchases.name} required>
                      Nome do item
                    </LabelWithTooltip>
                    <Input
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="Ex: Cerveja, Carvão, Gelo..."
                      className="mt-1"
                      data-testid="purchase-name"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <LabelWithTooltip tip={FIELD_TIPS.purchases.quantity}>
                        Quantidade
                      </LabelWithTooltip>
                      <Input
                        type="number"
                        value={formData.quantity}
                        onChange={(e) => setFormData({ ...formData, quantity: parseInt(e.target.value) || 1 })}
                        className="mt-1"
                        min={1}
                      />
                    </div>
                    <div>
                      <Label>Unidade</Label>
                      <Select
                        value={formData.unit}
                        onValueChange={(v) => setFormData({ ...formData, unit: v })}
                      >
                        <SelectTrigger className="mt-1">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="un">Unidade</SelectItem>
                          <SelectItem value="kg">Kg</SelectItem>
                          <SelectItem value="L">Litro</SelectItem>
                          <SelectItem value="cx">Caixa</SelectItem>
                          <SelectItem value="pct">Pacote</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div>
                    <LabelWithTooltip tip={FIELD_TIPS.purchases.estimated}>
                      Preço estimado (R$)
                    </LabelWithTooltip>
                    <div className="relative mt-1">
                      <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        type="number"
                        value={formData.estimated_price}
                        onChange={(e) => setFormData({ ...formData, estimated_price: e.target.value })}
                        placeholder="0,00"
                        className="pl-10"
                      />
                    </div>
                  </div>
                  <div>
                    <Label>Tipo de rateio</Label>
                    <Select
                      value={formData.purchase_type}
                      onValueChange={(v) => setFormData({ ...formData, purchase_type: v })}
                    >
                      <SelectTrigger className="mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="common">Para todos</SelectItem>
                        <SelectItem value="selective">Apenas quem consome</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <LabelWithTooltip tip={FIELD_TIPS.purchases.notes}>
                      Observações
                    </LabelWithTooltip>
                    <Input
                      value={formData.notes}
                      onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                      placeholder="Marca preferida, etc..."
                      className="mt-1"
                    />
                  </div>
                  <div className="flex gap-3 pt-2">
                    <Button type="button" variant="outline" className="flex-1 rounded-full" onClick={() => setDialogOpen(false)}>
                      Cancelar
                    </Button>
                    <Button type="submit" className="flex-1 rounded-full bg-orange-500 hover:bg-orange-600">
                      Adicionar
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="border-slate-100 dark:border-slate-700">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center">
                <Package className="w-5 h-5 text-slate-600 dark:text-slate-400" />
              </div>
              <div>
                <p className="text-sm text-slate-500 dark:text-slate-400">Total</p>
                <p className="font-bold text-lg text-slate-900 dark:text-white">{totalItems} itens</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-100 dark:border-slate-700">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-green-100 flex items-center justify-center">
                <Check className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500 dark:text-slate-400">Comprados</p>
                <p className="font-bold text-lg text-green-600">{purchasedItems}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-100 dark:border-slate-700">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-yellow-100 flex items-center justify-center">
                <ShoppingCart className="w-5 h-5 text-yellow-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500 dark:text-slate-400">Pendentes</p>
                <p className="font-bold text-lg text-yellow-600">{pendingItems}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-100 dark:border-slate-700">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-orange-100 flex items-center justify-center">
                <DollarSign className="w-5 h-5 text-orange-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500 dark:text-slate-400">Total gasto</p>
                <p className="font-bold text-lg text-orange-600">
                  R$ {actualTotal.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Progress */}
      <Card className="border-slate-100 dark:border-slate-700">
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-slate-700 dark:text-slate-300">Progresso das compras</span>
            <span className="text-sm text-slate-500 dark:text-slate-400">{progressPercent.toFixed(0)}%</span>
          </div>
          <Progress value={progressPercent} className="h-2" />
        </CardContent>
      </Card>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
        </div>
      ) : purchases.length === 0 ? (
        <Card className="border-slate-100 dark:border-slate-700 border-dashed">
          <CardContent className="py-12 text-center">
            <ShoppingBag className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <h3 className="font-heading font-bold text-lg text-slate-900 dark:text-white mb-2">
              Lista vazia
            </h3>
            <p className="text-slate-500 dark:text-slate-400">Adicione itens que precisam ser comprados</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6">
          {/* Pending Items */}
          {pendingPurchases.length > 0 && (
            <div>
              <h3 className="font-semibold text-slate-700 dark:text-slate-300 mb-3 flex items-center gap-2">
                <ShoppingCart className="w-4 h-4 text-yellow-500" />
                A comprar ({pendingPurchases.length})
              </h3>
              <div className="space-y-2">
                {pendingPurchases.map((item, index) => (
                  <motion.div
                    key={item.id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.05 }}
                  >
                    <Card className="border-slate-100 dark:border-slate-700">
                      <CardContent className="p-4">
                        <div className="flex items-center gap-4">
                          <div
                            className="w-6 h-6 rounded-full border-2 border-slate-300 cursor-pointer hover:border-green-500 transition-colors"
                            onClick={() => openMarkDialog(item)}
                          />
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-slate-900 dark:text-white">{item.name}</span>
                              <Badge variant="outline" className="text-xs">
                                {item.quantity} {item.unit}
                              </Badge>
                              {item.purchase_type === 'selective' && (
                                <Badge className="bg-purple-100 text-purple-700 text-xs">
                                  Seletivo
                                </Badge>
                              )}
                            </div>
                            {item.notes && (
                              <p className="text-sm text-slate-500 mt-1">{item.notes}</p>
                            )}
                          </div>
                          <div className="text-right">
                            {item.estimated_price && (
                              <p className="text-sm text-slate-600 dark:text-slate-400">
                                ~R$ {item.estimated_price.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                              </p>
                            )}
                          </div>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="text-red-500 h-8 w-8"
                            onClick={() => handleDelete(item.id)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                ))}
              </div>
            </div>
          )}

          {/* Completed Items */}
          {completedPurchases.length > 0 && (
            <div>
              <h3 className="font-semibold text-slate-700 dark:text-slate-300 mb-3 flex items-center gap-2">
                <Check className="w-4 h-4 text-green-500" />
                Comprados ({completedPurchases.length})
              </h3>
              <div className="space-y-2">
                {completedPurchases.map((item, index) => (
                  <motion.div
                    key={item.id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.05 }}
                  >
                    <Card className="border-green-100 bg-green-50/50">
                      <CardContent className="p-4">
                        <div className="flex items-center gap-4">
                          <div className="w-6 h-6 rounded-full bg-green-500 flex items-center justify-center">
                            <Check className="w-4 h-4 text-white" />
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-slate-700 dark:text-slate-300 line-through">{item.name}</span>
                              <Badge variant="outline" className="text-xs">
                                {item.quantity} {item.unit}
                              </Badge>
                            </div>
                            {item.purchased_by_name && (
                              <p className="text-sm text-slate-500 mt-1">
                                Comprado por {item.purchased_by_name}
                              </p>
                            )}
                          </div>
                          <div className="text-right">
                            <p className="font-medium text-green-700">
                              R$ {(item.actual_price || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                            </p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Mark as Purchased Dialog */}
      <Dialog open={markDialogOpen} onOpenChange={setMarkDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Marcar como comprado</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleMarkPurchased} className="space-y-4 mt-4">
            <div className="bg-slate-50 rounded-lg p-3">
              <p className="font-medium text-slate-900 dark:text-white">{selectedItem?.name}</p>
              <p className="text-sm text-slate-500 dark:text-slate-400">{selectedItem?.quantity} {selectedItem?.unit}</p>
            </div>
            <div>
              <Label>Preço pago (R$) *</Label>
              <div className="relative mt-1">
                <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  type="number"
                  value={markPrice}
                  onChange={(e) => setMarkPrice(e.target.value)}
                  placeholder="0,00"
                  className="pl-10"
                  step="0.01"
                />
              </div>
            </div>
            <div className="flex gap-3">
              <Button type="button" variant="outline" className="flex-1 rounded-full" onClick={() => setMarkDialogOpen(false)}>
                Cancelar
              </Button>
              <Button type="submit" className="flex-1 rounded-full bg-green-500 hover:bg-green-600">
                <Receipt className="w-4 h-4 mr-2" />
                Confirmar
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
